/**
 * useWizardState — Phase 27 refactor.
 *
 * Single source of truth for the deal wizard. Every state variable, every
 * useEffect, every callback that used to live inline in DealWizard.jsx is
 * preserved verbatim here. Zero behavior change vs. the pre-refactor file.
 *
 * Steps consume the return value as a single `wizard` prop and pull only
 * what they need — that's the entire point of this refactor.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { useFmt } from "@/lib/useFmt";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCountryList } from "@/components/CountryPicker";
import { DEAL_TYPE_IDS, DEAL_TYPE_DEFAULT, LAST_DEAL_TYPE_KEY } from "@/components/deal/DealTypePicker";

import { DEFAULT_FORM, FIELD_LABELS, LAST_STEP, STEPS, STORAGE_KEY, asNumber, readDraft } from "./_shared";

export default function useWizardState() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const { t } = useTranslation();
  const fmt = useFmt();
  const { defaultCountry } = usePrefs();
  const { user } = useAuth();
  const unitSystem = user?.unit_system || "imperial";
  const countries = useCountryList();

  // Lazy initializers prevent the previous race between restore-effect and persist-effect:
  // on first render, we already see what sessionStorage holds, so the very first persist
  // write below stores the SAME value back, never the default.
  const initialDraft = isEdit ? null : readDraft();
  // In edit mode, skip Step 1 (Type) — deal_type is already locked. New
  // mode starts at Step 1 to force the guided picker.
  const [step, setStep] = useState(isEdit ? 2 : (initialDraft?.step || 1));
  const [maxReached, setMaxReached] = useState(isEdit ? LAST_STEP : (initialDraft?.maxReached || 1));
  const [form, setForm] = useState(initialDraft?.form || DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  // Country/state: user-touched flag so we don't override manual selections via geocode.
  const [countryUserTouched, setCountryUserTouched] = useState(false);
  const [autofillHint, setAutofillHint] = useState(null); // {country_name}

  // Phase 11 — property autofill state. Tracks the in-flight request and
  // the last-applied snapshot so we can offer "Undo" for 10 seconds.
  const [autofillLoading, setAutofillLoading] = useState(false);
  const [autofillUndo, setAutofillUndo] = useState(null); // {prev, filledKeys, expires}

  // Phase 11 hotfix — track which fields the user has actively edited so
  // autofill never clobbers user input. `DEFAULT_FORM` ships with non-zero
  // bedrooms/bathrooms/square_feet/property_type defaults; without this set
  // the "fill if empty" guard would refuse to overwrite those defaults and
  // autofill would silently no-op on every field except asking_price/rent.
  const [touchedFields, setTouchedFields] = useState(() => new Set());

  // Phase 17 — Source of the property-tax estimate currently shown on
  // Step 4. Set by autofillFromMarket when it writes facts.tax_amount_yearly.
  // Cleared as soon as the user edits the taxes_annual field manually.
  const [taxSource, setTaxSource] = useState(null); // one of TAX_SOURCE_LABEL keys
  const [taxSourceMeta, setTaxSourceMeta] = useState(null); // { rate_pct?: number }

  // Phase 17 — Regulations alert state. Fetched whenever city + state +
  // strategy are all present (debounced 600ms) and the feature flag is on.
  const [regulationsData, setRegulationsData] = useState(null);
  const [regulationsLoading, setRegulationsLoading] = useState(false);

  // Phase 13 — pending deal-type selection. The Step-0 picker requires the
  // user to ACTIVELY confirm with Continue; a pre-highlighted "last used"
  // option does not auto-advance. `dealType` is the canonical confirmed
  // value used by submission; `pendingDealType` mirrors the radio state.
  const [dealType, setDealType] = useState(() => {
    // Confirmed type is only seeded from the draft (which is set after
    // confirm). Fresh-load = unconfirmed.
    return initialDraft?.confirmedDealType || (isEdit ? DEAL_TYPE_DEFAULT : "");
  });
  const [pendingDealType, setPendingDealType] = useState(() => {
    if (initialDraft?.confirmedDealType) return initialDraft.confirmedDealType;
    if (typeof window === "undefined") return "";
    return ""; // never auto-select on fresh load
  });
  const lastUsedDealType = (() => {
    if (typeof window === "undefined") return null;
    try {
      const saved = window.localStorage?.getItem(LAST_DEAL_TYPE_KEY);
      return DEAL_TYPE_IDS.includes(saved) ? saved : null;
    } catch (e) { return null; }
  })();
  useEffect(() => {
    if (typeof window === "undefined" || !dealType) return;
    // Batch AV — localStorage write categorised as PREFERENCE.
    // `LAST_DEAL_TYPE_KEY` is a UI hint ("which deal type did this
    // user pick last time?") used to pre-highlight a radio. It is
    // NOT auth or PII; reading it via an XSS payload yields a
    // single string like "rental". OK to keep in localStorage.
    try { window.localStorage.setItem(LAST_DEAL_TYPE_KEY, dealType); } catch (e) {
      // Phase 36 Batch 1 — don't swallow silently. localStorage is
      // disabled in private browsing modes / quota-exceeded states; we
      // still want to know in the console without blocking the user.
      console.warn("[useWizardState] persist LAST_DEAL_TYPE failed:", e);
    }
  }, [dealType]);

  // Phase 36 — Buyer/Seller analysis role. Mirrors `dealType` (top-level,
  // persisted in the session draft). Seeded from the user's
  // `default_analysis_role` prof setting; falls back to "buyer".
  const [analysisRole, setAnalysisRole] = useState(() => {
    if (initialDraft?.analysisRole === "buyer" || initialDraft?.analysisRole === "seller") {
      return initialDraft.analysisRole;
    }
    return ((user?.default_analysis_role) || "buyer").toLowerCase();
  });
  // Keep the role in sync with the user's saved default once the user
  // doc loads (only on a brand-new draft — never overwrite an active draft
  // or edit-mode hydration).
  useEffect(() => {
    if (isEdit) return;
    if (initialDraft) return;
    const u = (user?.default_analysis_role || "").toLowerCase();
    if (u === "buyer" || u === "seller") {
      setAnalysisRole(u);
    }
    // Batch AV — set-state-in-effect is INTENTIONAL here: we're syncing
    // local state from a downstream prop (user doc) the first time it
    // resolves. The `isEdit` and `initialDraft` guards make it run at
    // most once per mount, and only when there's no user-authored draft
    // to clobber. Refactoring to useMemo would lose the once-only
    // semantic that protects draft data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.default_analysis_role]);

  // Phase 12 — single-line address state. Mirrors the legacy split
  // address/city/state/zip but holds the canonical raw entry the user typed
  // (or Google Places returned). Hydrated from initialDraft / edit-fetch.
  const [addressRaw, setAddressRaw] = useState(() => {
    const draftAddr = initialDraft?.form?.property?.address || "";
    if (!draftAddr) return "";
    const c = initialDraft?.form?.property?.city || "";
    const s = initialDraft?.form?.property?.state || "";
    const z = initialDraft?.form?.property?.zip || "";
    return [draftAddr, c, [s, z].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  });


  useEffect(() => {
    if (!autofillUndo) return;
    const ms = autofillUndo.expires - Date.now();
    if (ms <= 0) {
      setAutofillUndo(null);
      return;
    }
    // Batch AV — set-state-in-effect is correct here: this is a timer
    // that EXPIRES the autofill-undo banner after `ms` milliseconds.
    // The setState fires from a timeout callback (NOT synchronously
    // during effect body), which is the canonical React pattern for
    // delayed transitions. The newer react-hooks/set-state-in-effect
    // rule (React 19+ plugin) flags it; webpack's eslint plugin
    // doesn't know that rule so no disable comment is added here.
    const handle = setTimeout(() => setAutofillUndo(null), ms);
    return () => clearTimeout(handle);
  }, [autofillUndo]);

  // Phase 17 — Regulations lookup, debounced 700ms. Fires whenever
  // city + state + strategy are all present. The backend caches 7d and
  // honours the `regulations_lookup_enabled` feature flag, so it returns
  // {enabled:false, data:null} when off — we hide the card in that case.
  useEffect(() => {
    const city = (form.property.city || "").trim();
    const state = (form.property.state || "").trim();
    const strategy = form.analysis.rental_strategy || "long_term";
    const country = (form.property.country || "US").toUpperCase();
    if (country !== "US") { setRegulationsData(null); return; }
    if (!city || !state) { setRegulationsData(null); return; }
    let alive = true;
    // Batch AV — set-state-in-effect inside the debounced async fetch
    // is INTENTIONAL: this is a search-as-you-type debounce. The
    // setLoading/setData fires AFTER the 700ms timer fires AND after
    // the network call resolves — never synchronously during render.
    // The `alive` flag prevents stale writes after unmount.
    const handle = setTimeout(async () => {
      try {
        setRegulationsLoading(true);
        const { data } = await api.get("/market/regulations", {
          params: { city, state, strategy },
        });
        if (!alive) return;
        if (data?.enabled && data.data) {
          setRegulationsData(data.data);
        } else {
          setRegulationsData(null);
        }
      } catch (err) {
        if (alive) setRegulationsData(null);
      } finally {
        if (alive) setRegulationsLoading(false);
      }
    }, 700);
    return () => { alive = false; clearTimeout(handle); };
  }, [form.property.city, form.property.state, form.property.country, form.analysis.rental_strategy]);

  // Phase 13 (D1) — Auto-fire autofill 600ms after parser produces a valid
  // US zip. Only triggers if the autofill targets haven't been user-touched.
  // Disabled in edit mode.
  const lastAutoZipRef = useRef(null);
  useEffect(() => {
    if (isEdit) return;
    const z = (form.property.zip || "").trim();
    const country = (form.property.country || "US").toUpperCase();
    if (country !== "US") return;
    if (!/^\d{5}$/.test(z)) return;
    if (!form.property.city || !form.property.state) return;
    if (lastAutoZipRef.current === z) return;  // don't repeat for the same zip
    // Skip if user already touched the autofill target fields directly.
    const targets = ["property.bedrooms", "property.bathrooms", "property.square_feet"];
    if (targets.every((k) => touchedFields.has(k))) return;
    const handle = setTimeout(() => {
      lastAutoZipRef.current = z;
      autofillFromMarket?.();
    }, 600);
    return () => clearTimeout(handle);
    // autofillFromMarket is captured via closure; we don't include it in deps
    // because it itself depends on form/touchedFields and we'd re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.property.zip, form.property.city, form.property.state, form.property.country, isEdit]);


  // Apply user's saved default country on a brand-new draft (don't overwrite restored).
  useEffect(() => {
    if (isEdit) return;
    if (initialDraft) return;
    if (defaultCountry && form.property.country !== defaultCountry) {
      setForm((f) => ({ ...f, property: { ...f.property, country: defaultCountry } }));
    }
    // Batch AV — same justification as the analysisRole sync above:
    // this is a one-shot upstream-prop → local-state mirror, guarded
    // by isEdit + initialDraft so it can't clobber user data. Effect
    // depends only on `defaultCountry` by design — the form check
    // inside the body uses the latest form via closure (acceptable
    // because we only WRITE the country field, never read others).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountry]);

  // ── Persist draft on every change (new-mode only) ─────────────────────────
  useEffect(() => {
    if (isEdit) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        form, step, maxReached, confirmedDealType: dealType, analysisRole,
      }));
    } catch (err) {
      // storage may be unavailable in some browsers/incognito — never fatal
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("DealWizard: sessionStorage write failed", err);
      }
    }
  }, [form, step, maxReached, isEdit, dealType, analysisRole]);

  // ── Fetch deal for edit mode ───────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/deals/${editId}`);
        // Phase 12 — hydrate deal_type + single-line address.
        if (DEAL_TYPE_IDS.includes(data.deal_type)) {
          setDealType(data.deal_type);
          setPendingDealType(data.deal_type);
        }
        // Phase 36 — hydrate buyer/seller perspective for edit mode.
        if (data.role === "buyer" || data.role === "seller") {
          setAnalysisRole(data.role);
        }
        const a = data.property?.address || "";
        const c = data.property?.city || "";
        const s = data.property?.state || "";
        const z = data.property?.zip || "";
        if (a || c || s || z) {
          setAddressRaw([a, c, [s, z].filter(Boolean).join(" ")].filter(Boolean).join(", "));
        }
        setForm({
          property: {
            address: data.property?.address || "",
            city: data.property?.city || "",
            state: data.property?.state || "",
            zip: data.property?.zip || "",
            country: data.property?.country || data.country || "US",
            property_type: data.property?.property_type || "single_family",
            units: data.property?.units || 1,
            bedrooms: data.property?.bedrooms || 0,
            bathrooms: data.property?.bathrooms || 0,
            square_feet: data.property?.square_feet || 0,
            // Phase 41 fix — the wizard's "Asking price" input is the
            // ORIGINAL list price, NOT the effective post-counter
            // basis. Post-Batch-AJ, `data.purchase_price` is the
            // EFFECTIVE (counter) price on any deal where a counter
            // has been applied; reading from it hydrated the asking
            // field with the counter value, so the next save crossed
            // the labels (the user typed an asking but it landed back
            // as the counter on re-save). Use the canonical fallback
            // chain that mirrors ai_service._canonical_ask:
            //   1. original_purchase_price  (set when counter flipped)
            //   2. property.asking_price    (immutable list)
            //   3. purchase_price           (no counter ever set → asking == effective)
            asking_price:
              data.original_purchase_price
              || data.property?.asking_price
              || data.purchase_price,
          },
          analysis: {
            monthly_rent: data.monthly_rent,
            taxes_annual: data.taxes_annual,
            insurance_annual: data.insurance_annual,
            hoa_monthly: data.hoa_monthly,
            utilities_monthly: data.utilities_monthly,
            repairs: data.repairs,
            vacancy_percent: data.vacancy_percent,
            management_percent: data.management_percent,
            down_payment_percent: data.down_payment_percent,
            interest_rate: data.interest_rate,
            // X1 — hydrate sensible loan defaults for legacy / cash-purchase deals.
            // 32 legacy deals have loan_term_years=null and 59 cash-purchase deals
            // have loan_term_years=0. Without these ?? fallbacks the wizard's
            // step-5 validation rejected the load on Save (lty<1) even though
            // the user never touched the field. Cash purchases additionally
            // skip the loan-block validation (see computeStepErrors below).
            loan_term_years: data.loan_term_years ?? 30,
            closing_costs: data.closing_costs,
            transfer_tax: data.transfer_tax || 0,
            // Phase 15
            description: data.description || "",
            counter_offer_price: data.counter_offer_price || 0,
            // Phase 17 — preserve cash / strategy when editing
            is_cash_purchase: !!data.is_cash_purchase,
            rental_strategy: data.rental_strategy || "long_term",
            // Phase 19 — preserve owned-property tracking when editing
            is_owned: !!data.is_owned,
            purchase_year: data.purchase_year ?? null,
            purchase_price_paid: data.purchase_price_paid ?? 0,
            original_loan_amount: data.original_loan_amount ?? 0,
            original_interest_rate: data.original_interest_rate ?? 0,
            original_loan_term_years: data.original_loan_term_years ?? 30,
            // Batch F — Realized Performance fields
            rental_start_year: data.rental_start_year ?? null,
            annual_rent_growth_percent: data.annual_rent_growth_percent ?? 3.0,
            current_estimated_value: data.current_estimated_value ?? 0,
            current_value_source: data.current_value_source || "manual",
            current_value_updated_at: data.current_value_updated_at ?? null,
            // Phase 36 Batch AA — Hydrate flip / BRRRR resale-strategy
            // fields on edit-load. Previously omitted, so editing a saved
            // flip deal showed ARV / repair-budget / holding-period etc.
            // as 0 every time the user re-opened the wizard — the user
            // would re-type them, save, and on the NEXT edit they'd
            // disappear again (bug 2: "ARV values aren't being saved").
            // Defaults mirror DEFAULT_FORM.analysis so the form shape
            // never changes between new and edit mode.
            arv: data.arv ?? 0,
            repair_budget: data.repair_budget ?? 0,
            holding_period_months: data.holding_period_months ?? 6,
            holding_costs_monthly: data.holding_costs_monthly ?? 0,
            selling_costs_pct: data.selling_costs_pct ?? 8,
            cash_purchase: !!(data.cash_purchase || data.is_cash_purchase),
            refi_ltv_pct: data.refi_ltv_pct ?? 75,
            refi_interest_rate: data.refi_interest_rate ?? 7,
            refi_loan_term_years: data.refi_loan_term_years ?? 30,
            refi_closing_costs: data.refi_closing_costs ?? 0,
            stabilization_months: data.stabilization_months ?? 6,
            // Phase 44 Batch BF1 — hydrate special-assessments array
            // on edit-load. Defaults to [] so the field is always
            // controllable.
            special_assessments: Array.isArray(data.special_assessments)
              ? data.special_assessments
              : [],
          },
          // Phase 36 — hydrate contact info on edit-load.
          contact: {
            listing_type: data.listing_type || "unknown",
            contact_name: data.contact_name || "",
            contact_brokerage: data.contact_brokerage || "",
            contact_email: data.contact_email || "",
            contact_phone: data.contact_phone || "",
            contact_cell: data.contact_cell || "",
            contact_phone_same_as_cell: !!data.contact_phone_same_as_cell,
            contact_notes: data.contact_notes || "",
            listing_url: data.listing_url || "",
          },
        });
        setMaxReached(LAST_STEP);
      } catch (e) {
        toast.error("Could not load deal for editing.");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [editId, isEdit, navigate]);

  // Phase 12 — when SingleAddressInput emits parsed parts, sync them into
  // form.property WITHOUT marking those fields as touched (because the user
  // didn't directly edit each one — they typed one combined string). This
  // preserves the autofill-overrides-untouched invariant from Phase 11.
  const applyParsedAddress = useCallback((rawValue, parsed) => {
    setAddressRaw(rawValue);
    setForm((f) => {
      // Phase 36 Batch AD — when the parser pulls a state/ZIP out of
      // the address text and the structured fields are still empty,
      // surface a "we filled this for you" toast + posthog event so
      // users notice the autopopulate. This is the only place we can
      // observe the transition cleanly (the parser runs on every
      // keystroke; this callback is the bottleneck).
      const prevState = f.property.state || f.property.region_code || "";
      const prevZip = f.property.zip || f.property.postal_code || "";
      const newState = parsed?.state || "";
      const newZip = parsed?.zip || "";
      const filledState = !prevState && !!newState;
      const filledZip = !prevZip && !!newZip;
      if (filledState || filledZip) {
        const filled = [];
        if (filledState) filled.push("State");
        if (filledZip) filled.push("ZIP");
        // Debounce to next tick so the toast doesn't fight the keystroke.
        setTimeout(() => {
          toast.success(`Filled ${filled.join(" & ")} from your address`);
          try {
            window.posthog?.capture?.("address_autoparsed", {
              filled_state: filledState,
              filled_zip: filledZip,
              source: "address_text_parser",
            });
          } catch {
            /* posthog blocked — ignore */
          }
        }, 0);
      }
      return {
        ...f,
        property: {
          ...f.property,
          address: parsed?.street ? (parsed.unit ? `${parsed.street} ${parsed.unit}` : parsed.street) : rawValue,
          city: parsed?.city || f.property.city,
          // Phase 36 Batch AD bug-fix — keep the GlobalLocationFields
          // UI (which reads region_code/postal_code) in sync with the
          // legacy state/zip fields the parser populates. Without this
          // mirror the State dropdown sits empty even when the user
          // typed "..., FL 33143" — the original bug screenshotted.
          state: parsed?.state || f.property.state,
          region_code: parsed?.state || f.property.region_code,
          zip: parsed?.zip || f.property.zip,
          postal_code: parsed?.zip || f.property.postal_code,
        },
      };
    });
  }, []);

  const setProp = useCallback((k, v) => {
    setForm((f) => ({ ...f, property: { ...f.property, [k]: v } }));
    setTouchedFields((prev) => {
      const fieldKey = `property.${k}`;
      if (prev.has(fieldKey)) return prev;
      const next = new Set(prev);
      next.add(fieldKey);
      return next;
    });
  }, []);

  const setAnalysis = useCallback((k, v) => {
    setForm((f) => ({ ...f, analysis: { ...f.analysis, [k]: v } }));
    setTouchedFields((prev) => {
      const fieldKey = `analysis.${k}`;
      if (prev.has(fieldKey)) return prev;
      const next = new Set(prev);
      next.add(fieldKey);
      return next;
    });
    // Phase 17 — clear the tax-source label as soon as the user touches the
    // tax field directly. From that point on it's a "Manual entry".
    if (k === "taxes_annual") {
      setTaxSource(null);
      setTaxSourceMeta(null);
    }
  }, []);

  // Phase 36 — Listing & contact info setter. Treated as its own top-level
  // slice (parallel to property/analysis) because the backend persists it
  // on the deal doc rather than in either nested struct. Toggling
  // contact_phone_same_as_cell=true immediately clears the cell field so
  // the wizard never persists a stale orphan cell number.
  const setContact = useCallback((k, v) => {
    setForm((f) => {
      const nextContact = { ...(f.contact || {}), [k]: v };
      if (k === "contact_phone_same_as_cell" && v === true) {
        nextContact.contact_cell = "";
      }
      return { ...f, contact: nextContact };
    });
    setTouchedFields((prev) => {
      const fieldKey = `contact.${k}`;
      if (prev.has(fieldKey)) return prev;
      const next = new Set(prev);
      next.add(fieldKey);
      return next;
    });
  }, []);

  // Phase 11 hotfix — Smart Address Autofill.
  //
  // Previous implementation guarded with `if (!target[key] || target[key] === 0)`
  // which silently no-op'd against `DEFAULT_FORM`'s prefilled values
  // (bedrooms=3, bathrooms=2, square_feet=1500, property_type="single_family").
  //
  // New invariant: a field is "untouched" if the user has not interacted with
  // it via setProp/setAnalysis since the wizard mounted. Autofill OVERRIDES
  // untouched fields with API data, PRESERVES touched fields.
  const autofillFromMarket = useCallback(async () => {
    const p = form.property;
    if ((p.country || "US").toUpperCase() !== "US") {
      toast.info("Autofill is US-only for now.");
      return;
    }
    if (!p.address?.trim() || !p.city?.trim() || !p.state?.trim()) {
      toast.error("Enter street address, city, and state first.");
      return;
    }
    setAutofillLoading(true);
    try {
      const params = { address: p.address.trim(), country: "US" };
      if (p.city) params.city = p.city.trim();
      if (p.state) params.state = p.state.trim();
      if (p.zip) params.zip = p.zip.trim();
      const { data } = await api.get("/market/property-lookup", { params });
      // eslint-disable-next-line no-console
      console.debug("[autofill] received", data);
      const facts = data?.facts;
      if (!facts) {
        toast.info("No market data found for this address.");
        return;
      }

      // Plan writes BEFORE mutating state. Each write target is keyed by
      // `<scope>.<field>` so we can check the touched set.
      const writes = { property: {}, analysis: {} };
      const filled = [];
      const isEmptyApi = (v) => v == null || v === "" || v === 0 || v === "0";

      const tryWrite = (scope, key, value, friendly) => {
        if (isEmptyApi(value)) return;
        if (touchedFields.has(`${scope}.${key}`)) return;
        writes[scope][key] = value;
        filled.push(friendly);
      };

      tryWrite("property", "bedrooms", facts.bedrooms, "bedrooms");
      tryWrite("property", "bathrooms", facts.bathrooms, "bathrooms");
      tryWrite("property", "square_feet", facts.square_feet, "square feet");
      tryWrite("property", "property_type", facts.property_type, "property type");
      tryWrite("property", "asking_price", facts.estimated_value, "asking price");
      tryWrite("analysis", "monthly_rent", facts.estimated_rent, "monthly rent");
      // Phase 17 — property tax annual amount (preferred from ATTOM, else
      // county-rate estimate, else national-median fallback). Source is
      // captured in `taxSource` for the Step 4 visual label.
      tryWrite("analysis", "taxes_annual", facts.tax_amount_yearly, "property taxes");

      // eslint-disable-next-line no-console
      console.debug("[autofill] writing", writes, "filled fields:", filled);

      if (filled.length === 0) {
        toast.info("Your inputs were preserved — every relevant field has been edited.");
        return;
      }

      // Snapshot exact PRE-WRITE values for undo (only the fields we touch).
      const prevSnapshot = { property: {}, analysis: {} };
      for (const k of Object.keys(writes.property)) {
        prevSnapshot.property[k] = form.property[k];
      }
      for (const k of Object.keys(writes.analysis)) {
        prevSnapshot.analysis[k] = form.analysis[k];
      }

      setForm((f) => ({
        property: { ...f.property, ...writes.property },
        analysis: { ...f.analysis, ...writes.analysis },
      }));
      // Phase 17 — capture the tax source label whenever the autofill
      // actually wrote a tax amount. We compare keys against the writes set
      // because if the user had already touched taxes_annual we skipped it.
      if (Object.prototype.hasOwnProperty.call(writes.analysis, "taxes_annual") && facts.tax_source) {
        setTaxSource(facts.tax_source);
        setTaxSourceMeta({ rate_pct: facts.tax_rate_pct ?? null });
      }
      // IMPORTANT: do NOT mark autofilled fields as "touched" — the user has
      // not edited them yet. If they edit AFTER autofill, that subsequent
      // setProp/setAnalysis call will mark them touched and any future
      // autofill click will preserve those edits.

      const srcs = (data.sources || []).join(" + ") || "market data";
      toast.success(
        `Filled ${filled.length} field${filled.length === 1 ? "" : "s"} from ${srcs}`,
      );
      setAutofillUndo({
        prev: prevSnapshot,
        filledKeys: filled,
        expires: Date.now() + 10000,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Autofill failed");
    } finally {
      setAutofillLoading(false);
    }
  }, [form, touchedFields]);

  const undoAutofill = useCallback(() => {
    // Batch AV — the newer react-hooks/immutability and
    // /preserve-manual-memoization rules (React 19+ plugin) flag this
    // callback because `autofillUndo` is both in deps AND closure-read
    // inside. That's intentional: each new undo SHOULD produce a fresh
    // callback that closes over THAT undo's snapshot. Hoisting to a
    // ref would let a stale callback fire after a NEW autofill arrived.
    // The setForm call uses the functional updater so React's state
    // update is already immutable — the rule is over-zealous.
    // Webpack's older eslint plugin doesn't know those rules so we
    // can't silence them here — behaviour is correct.
    if (!autofillUndo) return;
    setForm((f) => ({
      property: { ...f.property, ...autofillUndo.prev.property },
      analysis: { ...f.analysis, ...autofillUndo.prev.analysis },
    }));
    setAutofillUndo(null);
    toast.success("Autofill reverted");
  }, [autofillUndo]);

  const onCountryChange = useCallback((cc) => {
    setCountryUserTouched(true);
    setProp("country", cc);
    if (form.property.state) setProp("state", ""); // clear state when country changes
  }, [form.property.state, setProp]);

  // Tax-defaults: refetch + autofill whenever country/state/price changes.
  const taxFetchKey = `${form.property.country}|${form.property.state || ""}|${form.property.asking_price}`;
  useEffect(() => {
    if (isEdit) return;
    const price = Number(form.property.asking_price);
    if (!price || price <= 0) return;
    let alive = true;
    const params = { country: form.property.country, purchase_price: price };
    if (form.property.state) params.state = form.property.state;
    api.get("/tax-defaults", { params }).then(({ data }) => {
      if (!alive) return;
      setAutofillHint({ country_name: data.country_name });
      // Only fill if currently zero (don't overwrite user-edited values).
      setForm((f) => {
        const a = { ...f.analysis };
        if (!a.taxes_annual) a.taxes_annual = data.default_taxes_annual;
        if (!a.transfer_tax) a.transfer_tax = data.default_transfer_tax;
        if (!a.closing_costs) a.closing_costs = data.default_closing_costs;
        return { ...f, analysis: a };
      });
    }).catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxFetchKey, isEdit]);

  // Nominatim geocode auto-detect — debounced. Only fires if user hasn't manually picked country.
  const geocodeTimer = useRef(null);
  useEffect(() => {
    if (isEdit) return;
    if (countryUserTouched) return;
    const addr = form.property.address.trim();
    if (addr.length < 8) return;
    if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
    geocodeTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/geocode/country", { params: { address: addr } });
        if (data?.country_code && data.confidence === "high") {
          // Only apply if it's a supported country and different from current.
          const supported = countries.some((c) => c.code === data.country_code);
          if (supported && data.country_code !== form.property.country) {
            setProp("country", data.country_code);
            toast(t("dealDetail.country.auto_detected", { country: data.country_name }));
          }
        }
      } catch (err) {
        // Non-fatal — geocoding is a UX nicety, not a blocker.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("DealWizard: geocode failed", err);
        }
      }
    }, 600);
    return () => clearTimeout(geocodeTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.property.address, countryUserTouched]);

  // The old "auto-fill 2% closing-costs on first price change" effect is removed in Phase 5;
  // tax-defaults now provides country-correct closing costs (US 2%, MX 6%, ES 2.5%, …).

  // ── Validation per step ────────────────────────────────────────────────────
  //
  // X1 — Split into a pure compute helper + a stateful wrapper. The pure
  // helper returns a fresh errors object for any step without touching
  // React state, so `submit` can collect errors across all steps without
  // each call overwriting the previous one (the pre-fix behaviour). The
  // wrapper preserves the original signature for the `next` button.
  //
  // Cash-purchase deals legitimately store loan_term_years=0 and
  // interest_rate=0 — skipping the loan block in that case kept those
  // deals editable without forcing the user to type fake loan terms.
  const computeStepErrors = useCallback(
    (s) => {
      const e = {};
      const p = form.property;
      const a = form.analysis;
      if (s === 2) {
        if (!p.address.trim()) e.address = "Required";
        if (!p.city.trim()) e.city = "Required";
        if (asNumber(p.asking_price) <= 0) e.asking_price = "Must be greater than 0";
        if (asNumber(p.units) < 1) e.units = "At least 1";
      }
      if (s === 3) {
        if (asNumber(a.monthly_rent) < 0) e.monthly_rent = "Must be ≥ 0";
        // Phase 44 Batch BF2 — ARV is REQUIRED for flips. Pre-BF2 the
        // wizard let a user save a flip with arv=0, which then routed
        // through compute_flip and produced a "97/100 Strong Flip"
        // score on the AVM-discrepancy signal alone. Forcing arv > 0
        // closes that loophole at the input gate.
        if (dealType === "flip") {
          if (asNumber(a.arv) <= 0) {
            e.arv = "ARV is required for a flip — what will you sell it for?";
          }
          if (asNumber(a.repair_budget) < 0) {
            e.repair_budget = "Must be ≥ 0";
          }
        }
        // Multifamily must have at least one row with units & rent
        if (dealType === "multifamily") {
          const mix = Array.isArray(form.unit_mix) ? form.unit_mix : [];
          const totalUnits = mix.reduce((acc, r) => acc + (Number(r.count) || 0), 0);
          if (totalUnits < 1) e.unit_mix = "Add at least one unit row with count ≥ 1";
        }
      }
      if (s === 4) {
        for (const k of ["taxes_annual", "insurance_annual", "hoa_monthly", "utilities_monthly", "repairs"]) {
          if (asNumber(a[k]) < 0) e[k] = "Must be ≥ 0";
        }
        if (asNumber(a.vacancy_percent) < 0 || asNumber(a.vacancy_percent) > 100) e.vacancy_percent = "0–100";
        if (asNumber(a.management_percent) < 0 || asNumber(a.management_percent) > 100) e.management_percent = "0–100";
      }
      if (s === 5) {
        // X1 — cash purchases have no loan terms. Skip the down-payment /
        // interest-rate / loan-term-years block so legacy cash deals
        // (with lty=0 and ir=0) don't fail Save with a non-actionable toast.
        // closing_costs is still validated because cash deals can have them.
        if (!a.is_cash_purchase) {
          if (asNumber(a.down_payment_percent) < 0 || asNumber(a.down_payment_percent) > 100) e.down_payment_percent = "0–100";
          if (asNumber(a.interest_rate) < 0 || asNumber(a.interest_rate) > 50) e.interest_rate = "0–50";
          if (asNumber(a.loan_term_years) < 1 || asNumber(a.loan_term_years) > 60) e.loan_term_years = "1–60";
        }
        if (asNumber(a.closing_costs) < 0) e.closing_costs = "Must be ≥ 0";
      }
      return e;
    },
    [form, dealType]
  );

  const validateStep = useCallback(
    (s) => {
      const e = computeStepErrors(s);
      setErrors(e);
      return Object.keys(e).length === 0;
    },
    [computeStepErrors]
  );

  const next = useCallback(() => {
    if (!validateStep(step)) return;
    const ns = Math.min(LAST_STEP, step + 1);
    setStep(ns);
    setMaxReached((m) => Math.max(m, ns));
  }, [step, validateStep]);

  const back = useCallback(() => setStep((s) => Math.max(1, s - 1)), []);

  const jump = useCallback((id) => {
    if (id <= maxReached) setStep(id);
  }, [maxReached]);

  // Phase 27 — used by Step 0 confirmation. Preserves the exact behaviour
  // the original inline closure had (sets confirmed dealType + advances + bumps maxReached).
  const confirmDealType = useCallback((id) => {
    setDealType(id);
    setPendingDealType(id);
    setStep(2);
    setMaxReached((m) => Math.max(m, 2));
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────────
  // Multifamily: derive effective rent and pass unit_mix through to engine.
  const payload = useMemo(() => {
    const a = form.analysis;
    const isMF = dealType === "multifamily";
    const mix = Array.isArray(form.unit_mix) ? form.unit_mix : null;
    // Effective rent for MF = Σ count × rent × (1 − vacancy/100). Falls
    // back to raw monthly_rent for non-MF or when mix is empty.
    let effectiveRent = Number(a.monthly_rent) || 0;
    if (isMF && mix && mix.length) {
      effectiveRent = mix.reduce((acc, r) => {
        const c = Number(r.count) || 0;
        const rent = Number(r.rent) || 0;
        const v = Number(r.vacancy_pct) || 0;
        return acc + c * rent * (1 - v / 100);
      }, 0);
    }
    return {
      deal_type: dealType,
      // Phase 36 — buyer/seller perspective for THIS deal. Defaulted from
      // user.default_analysis_role; user can override per-deal on Step 1.
      role: analysisRole,
      property: {
        address: form.property.address.trim(),
        city: form.property.city.trim(),
        state: form.property.state?.trim() || null,
        zip: form.property.zip?.trim() || null,
        country: (form.property.country || "US").toUpperCase(),
        property_type: form.property.property_type,
        units: isMF && mix ? Math.max(1, mix.reduce((acc, r) => acc + (Number(r.count) || 0), 0)) : (Number(form.property.units) || 1),
        bedrooms: Number(form.property.bedrooms) || 0,
        bathrooms: Number(form.property.bathrooms) || 0,
        square_feet: Number(form.property.square_feet) || 0,
        asking_price: Number(form.property.asking_price) || 0,
        // Phase 36 Batch X — explicit local-currency override. Empty
        // means "let the server derive from country" (preferred path).
        property_currency: (countries.find(
          (c) => c.code === (form.property.country || "US").toUpperCase(),
        )?.currency) || null,
      },
      analysis: {
        purchase_price: Number(form.property.asking_price) || 0,
        monthly_rent: Math.round(effectiveRent * 100) / 100,
        taxes_annual: Number(a.taxes_annual) || 0,
        insurance_annual: Number(a.insurance_annual) || 0,
        hoa_monthly: Number(a.hoa_monthly) || 0,
        utilities_monthly: Number(a.utilities_monthly) || 0,
        repairs: Number(a.repairs) || 0,
        vacancy_percent: Number(a.vacancy_percent) || 0,
        management_percent: Number(a.management_percent) || 0,
        down_payment_percent: Number(a.down_payment_percent) || 0,
        interest_rate: Number(a.interest_rate) || 0,
        loan_term_years: Number(a.loan_term_years) || 30,
        closing_costs: Number(a.closing_costs) || 0,
        transfer_tax: Number(a.transfer_tax) || 0,
        // Flip / BRRRR pass-throughs (engine ignores when not relevant)
        arv: Number(a.arv) || 0,
        repair_budget: Number(a.repair_budget) || 0,
        holding_period_months: Number(a.holding_period_months) || 0,
        holding_costs_monthly: Number(a.holding_costs_monthly) || 0,
        selling_costs_pct: Number(a.selling_costs_pct) || 0,
        cash_purchase: !!(a.cash_purchase || a.is_cash_purchase),
        // Phase 44 Batch BF2 — ARV data-confidence tier. Drives the
        // 5-pt subscore in `compute_flip` and the ARV-source pill on
        // the Flip Financials tab.
        arv_source: a.arv_source || "unverified",
        refi_ltv_pct: Number(a.refi_ltv_pct) || 0,
        refi_interest_rate: Number(a.refi_interest_rate) || 0,
        refi_loan_term_years: Number(a.refi_loan_term_years) || 30,
        refi_closing_costs: Number(a.refi_closing_costs) || 0,
        stabilization_months: Number(a.stabilization_months) || 0,
        unit_mix: isMF && mix ? mix : undefined,
        // Phase 44 Batch BF1 — special assessments pass-through.
        // Cast strings → numbers per row and drop empty rows so the
        // backend Pydantic model only sees populated entries. null
        // outstanding_balance is preserved (engine auto-derives at
        // read time).
        special_assessments: (Array.isArray(a.special_assessments)
          ? a.special_assessments
          : []
        )
          .filter(
            (sa) =>
              Number(sa.monthly_amount) > 0 &&
              Number(sa.payments_remaining) > 0,
          )
          .map((sa) => ({
            name: (sa.name || "").trim() || null,
            monthly_amount: Number(sa.monthly_amount),
            payments_remaining: Math.round(Number(sa.payments_remaining)),
            total_payments:
              sa.total_payments != null && sa.total_payments !== ""
                ? Math.round(Number(sa.total_payments))
                : null,
            outstanding_balance:
              sa.outstanding_balance != null && sa.outstanding_balance !== ""
                ? Number(sa.outstanding_balance)
                : null,
            pay_off_at_closing: !!sa.pay_off_at_closing,
            start_date: sa.start_date || null,
          })),
        // Phase 15 — counter-offer + description pass-through
        description: (a.description || "").trim() || null,
        counter_offer_price: Number(a.counter_offer_price) > 0 ? Number(a.counter_offer_price) : null,
        // Phase 17 — Cash purchase + rental strategy
        is_cash_purchase: !!a.is_cash_purchase,
        rental_strategy: a.rental_strategy || "long_term",
        // Phase 19 — Owned-property tracking. When is_owned is FALSE we
        // intentionally send the four sub-fields as null so a previously-owned
        // deal can be unmarked without leaving dangling values.
        is_owned: !!a.is_owned,
        purchase_year: a.is_owned && a.purchase_year ? Number(a.purchase_year) : null,
        purchase_price_paid: a.is_owned && Number(a.purchase_price_paid) > 0 ? Number(a.purchase_price_paid) : null,
        original_loan_amount: a.is_owned && Number(a.original_loan_amount) > 0 ? Number(a.original_loan_amount) : null,
        original_interest_rate: a.is_owned && Number(a.original_interest_rate) > 0 ? Number(a.original_interest_rate) : null,
        original_loan_term_years: a.is_owned && Number(a.original_loan_term_years) > 0 ? Number(a.original_loan_term_years) : null,
        // Batch F — Realized Performance fields. Same null-on-unowned pattern.
        rental_start_year: a.is_owned && a.rental_start_year ? Number(a.rental_start_year) : null,
        annual_rent_growth_percent: a.is_owned && Number(a.annual_rent_growth_percent) >= 0 ? Number(a.annual_rent_growth_percent) : null,
        current_estimated_value: a.is_owned && Number(a.current_estimated_value) > 0 ? Number(a.current_estimated_value) : null,
        current_value_source: a.is_owned ? (a.current_value_source || "manual") : null,
        current_value_updated_at: a.is_owned ? (a.current_value_updated_at || null) : null,
        // Phase 36 Batch X — GMIA YES-path. Only forward when the
        // user actually flipped "did_exchange_funds=true" AND typed
        // a positive rate. Backend auto-locks today's rate otherwise.
        purchase_exchange_rate:
          a.did_exchange_funds && Number(a.purchase_exchange_rate) > 0
            ? Number(a.purchase_exchange_rate)
            : null,
        purchase_date:
          a.did_exchange_funds && a.purchase_date ? a.purchase_date : null,
      },
      // Phase 36 — listing & contact info. Sent top-level on the deal
      // (parallel to deal_type) because backend persists it on the deal
      // doc itself. Server normalises phones + emails so we send raw.
      contact: (() => {
        const c = form.contact || {};
        const trim = (v) => (typeof v === "string" ? v.trim() : v);
        return {
          listing_type: c.listing_type || "unknown",
          contact_name: trim(c.contact_name) || null,
          contact_brokerage: trim(c.contact_brokerage) || null,
          contact_email: trim(c.contact_email) || null,
          contact_phone: trim(c.contact_phone) || null,
          contact_cell: trim(c.contact_cell) || null,
          contact_phone_same_as_cell: !!c.contact_phone_same_as_cell,
          contact_notes: trim(c.contact_notes) || null,
          listing_url: trim(c.listing_url) || null,
        };
      })(),
    };
  }, [form, dealType, analysisRole, countries]);

  const submit = useCallback(async () => {
    // X1 — Compute errors across all gated steps in one shot so we can:
    //   (a) jump the wizard back to the FIRST failing step
    //   (b) tell the user the specific step + field that's blocking save
    //   (c) list ALL failing fields in the toast description when >1
    // The previous code called validateStep(2|3|4|5) sequentially and
    // setErrors(...) overwrote on each call → the user landed on Step 6
    // staring at a generic toast with no way to find the problem.
    const allErrors = {
      2: computeStepErrors(2),
      3: computeStepErrors(3),
      4: computeStepErrors(4),
      5: computeStepErrors(5),
    };
    const failingSteps = [2, 3, 4, 5].filter((s) => Object.keys(allErrors[s]).length > 0);
    if (failingSteps.length > 0) {
      const firstStep = failingSteps[0];
      const firstStepErrors = allErrors[firstStep];
      const firstField = Object.keys(firstStepErrors)[0];
      const firstFieldLabel = FIELD_LABELS[firstField] || firstField;
      const firstStepTitle = STEPS[firstStep - 1].title;

      // Build a flat list of "Step N (Title): Field — message" for the toast
      // description when more than one field is failing across all steps.
      const allFailures = [];
      for (const s of failingSteps) {
        const stepTitle = STEPS[s - 1].title;
        for (const [field, msg] of Object.entries(allErrors[s])) {
          const label = FIELD_LABELS[field] || field;
          allFailures.push(`Step ${s} (${stepTitle}): ${label} — ${msg}`);
        }
      }

      // Jump to the first failing step and surface its inline red errors.
      setStep(firstStep);
      setErrors(firstStepErrors);

      const title = `Step ${firstStep} (${firstStepTitle}): ${firstFieldLabel} — ${firstStepErrors[firstField]}`;
      if (allFailures.length > 1) {
        toast.error(title, {
          description: allFailures.slice(1).join("\n"),
          duration: 8000,
          // sonner respects \n via whitespace-pre-line so the description
          // reads as a bulleted-ish list rather than a single mashed line.
          style: { whiteSpace: "pre-line" },
        });
      } else {
        toast.error(title, { duration: 6000 });
      }
      return;
    }
    setSubmitting(true);
    try {
      let data;
      if (isEdit) {
        const res = await api.patch(`/deals/${editId}`, payload);
        data = res.data;
        toast.success("Deal updated.");
      } else {
        const res = await api.post("/deals", payload);
        data = res.data;
        sessionStorage.removeItem(STORAGE_KEY);
        toast.success(`Deal analyzed — verdict: ${data.verdict}`);
      }
      // Phase 36 — surface server-side soft warnings (e.g., realtor
      // saved without a contact name) as a non-blocking toast right
      // after the success message.
      const warnings = Array.isArray(data?.warnings) ? data.warnings : [];
      for (const w of warnings) {
        toast.warning(w, { duration: 7000 });
      }
      navigate(`/deals/${isEdit ? editId : data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save deal. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [editId, isEdit, navigate, payload, computeStepErrors]);

  return {
    // routing
    navigate,
    editId,
    isEdit,
    // i18n & formatting
    t,
    fmt,
    unitSystem,
    countries,
    // step state
    step,
    setStep,
    maxReached,
    setMaxReached,
    next,
    back,
    jump,
    // form data
    form,
    setForm,
    errors,
    // loading / submission
    loading,
    submitting,
    // address
    addressRaw,
    applyParsedAddress,
    // setters
    setProp,
    setAnalysis,
    setContact,
    // country
    onCountryChange,
    autofillHint,
    // autofill
    autofillLoading,
    autofillUndo,
    autofillFromMarket,
    undoAutofill,
    // tax source label
    taxSource,
    taxSourceMeta,
    // regulations alert
    regulationsData,
    regulationsLoading,
    // deal type
    dealType,
    pendingDealType,
    setPendingDealType,
    lastUsedDealType,
    confirmDealType,
    // Phase 36 — buyer/seller analysis role
    analysisRole,
    setAnalysisRole,
    // submit
    submit,
  };
}
